import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatBadgeModule } from '@angular/material/badge';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatChipsModule } from '@angular/material/chips';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DrinksService } from '../../api/services/drinks.service';
import { AnalyticsService } from '../../api/services/analytics.service';
import { Drink } from '../../api/models/drink.model';
import { Observable, BehaviorSubject, forkJoin, debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material/table';
import { HeaderNavComponent } from "src/app/ui/header-nav/header-nav.component";
import { FooterComponent } from "src/app/ui/footer/footer.component";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { Category } from 'src/app/api/models';
import { environment } from '../../../environments/environment';
import { AuthService } from 'src/app/api/services/auth.service';

@Component({
  selector: 'app-bar-owner-dashboard',
  standalone: true,
  imports: [
    CommonModule, MatCardModule, MatButtonModule, MatIconModule, MatTableModule,
    MatDialogModule, MatFormFieldModule, MatInputModule, MatSelectModule,
    MatTabsModule, MatBadgeModule, MatSnackBarModule, MatToolbarModule,
    MatChipsModule, MatPaginatorModule, MatSortModule, MatCheckboxModule, FormsModule, ReactiveFormsModule,
    HeaderNavComponent,
    FooterComponent,
    MatProgressSpinner
],
  templateUrl: './bar-owner-dashboard.component.html',
  styles: []
})
export class BarOwnerDashboardComponent implements OnInit {
  drinks$: Observable<Drink[]>;
  dataSource = new MatTableDataSource<Drink>();
  displayedColumns: string[] = ['name', 'category', 'price', 'stock', 'status', 'actions'];
  selectedTabIndex = 0;
  showAddDrinkDialog = false;
  showBulkInventoryDialog = false;
  bulkOperation = 'add';
  bulkQuantity = 0;
  bulkCategoryFilter = '';
  categories: Category[] = [];

  isLoading = false;
  
  newDrink: Partial<Drink> = {
    name: '',
    categoryName: '', // Updated to categoryName
    price: 0,
    stock: 0,
    description: '',
    isAvailable: true
  };

  private drinksSubject = new BehaviorSubject<Drink[]>([]);
  totalRevenue = 0;
  inventoryValue = 0;
  searchQuery = '';
  private searchSubject = new Subject<string>();

  @ViewChild('deleteConfirmDialog') deleteConfirmDialog: any;
  drinkToDeleteId: number | null = null;

  constructor(
    private drinksService: DrinksService,
    private analyticsService: AnalyticsService,
    private snackBar: MatSnackBar,
    private router: Router,
    private dialog: MatDialog,
    private auth: AuthService
  ) {
    this.drinks$ = this.drinksService.getDrinks();
    this.drinksService.getCategories().subscribe(categories => this.categories = categories);
  }

  /**
   * The signed-in user's own bar, read from the token's bar_id claim.
   *
   * Five places in this file fell back to the literal 'BAR001' when a drink
   * carried no barId. That is one developer's fixture: on a real deployment no
   * such bar exists, so the tenancy guard rejects the write — or, worse, if it
   * ever did exist the row would land in somebody else's inventory.
   */
  barId: string | null = null;

  ngOnInit(): void {
    this.auth.getCurrentBarId().then(id => this.barId = id);
    this.loadData();
    this.loadInventoryValue();
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => query.trim()
        ? this.drinksService.searchDrinks({ name: query }).pipe(map(page => page.content || []))
        : this.drinksService.getDrinks())
    ).subscribe(drinks => {
      this.dataSource.data = drinks;
    });
  }

  onSearchChange(): void {
    this.searchSubject.next(this.searchQuery);
  }

  loadInventoryValue(): void {
    this.drinksService.getInventoryValue().subscribe({
      next: (value) => this.inventoryValue = value,
      error: () => {}
    });
  }

  patchStock(drink: Drink, newStock: number): void {
    this.drinksService.updateStockPut(drink.id, newStock).subscribe({
      next: (updated) => {
        drink.stock = updated.stock;
        drink.isAvailable = updated.isAvailable;
        this.snackBar.open('Stock updated', 'Close', { duration: 2000 });
        this.loadInventoryValue();
      },
      error: () => this.snackBar.open('Failed to update stock', 'Close', { duration: 2000 })
    });
  }

  reduceStockForDrink(drink: Drink, amount: number): void {
    this.drinksService.reduceStock(drink.id, amount).subscribe({
      next: (updated) => {
        drink.stock = updated.stock;
        drink.isAvailable = updated.isAvailable;
        this.snackBar.open(`Stock reduced by ${amount}`, 'Close', { duration: 2000 });
        this.loadInventoryValue();
      },
      error: () => this.snackBar.open('Failed to reduce stock', 'Close', { duration: 2000 })
    });
  }

  filterByCategory(categoryId: number): void {
    this.drinksService.getDrinksByCategory(categoryId).subscribe({
      next: (drinks) => this.dataSource.data = drinks,
      error: () => {}
    });
  }

  showAllDrinks(): void {
    this.dataSource.data = this.drinksSubject.value;
  }

  loadData(): void {
    this.isLoading = true;
    forkJoin({
      drinks: this.drinksService.getDrinks(),
      categories: this.drinksService.getCategories(),
      analytics: this.analyticsService.getSalesAnalytics()
    }).subscribe({
      next: (data) => {
        this.isLoading = false;
        this.categories = data.categories;
        
        // Enrich drinks with category names
        const enrichedDrinks = data.drinks.map(drink => {
          if (!drink.categoryName) {
            const category = this.categories.find(c => c.id === drink.categoryId);
            if (category) {
              drink.categoryName = category.name;
            }
          }
          (drink as any).selected = false;
          return drink;
        });

        this.drinksSubject.next(enrichedDrinks);
        this.dataSource.data = enrichedDrinks;
        this.totalRevenue = data.analytics.totalSales || 0;
      },
      error: (err) => {
        this.isLoading = false;
        if (!environment.production) {
          console.error('Error loading dashboard data:', err);
        }
      }
    });
  }

  onBack(): void {
    this.router.navigate(['/service-provider']);
  }

  onTabChange(event: any): void {
    this.selectedTabIndex = event.index;
  }

  getTotalDrinks(): number {
    return this.drinksSubject.value.length;
  }

  getLowStockItems(): number {
    return this.drinksSubject.value.filter(drink => drink.stock < 5 && drink.isAvailable).length;
  }

  getTotalRevenue(): number {
    return this.totalRevenue;
  }

  getTotalCategories(): number {
    const categories = new Set(this.drinksSubject.value.map(drink => drink.categoryName));
    return categories.size;
  }

  getCategoryStats(): any[] {
    return this.categories.map(category => {
      const drinks = this.drinksSubject.value.filter(drink => drink.categoryId === category.id);
      const totalStock = drinks.reduce((sum, drink) => sum + drink.stock, 0);
      const lowStock = drinks.filter(drink => drink.stock < 5).length;
      
      return {
        category: category.name,
        totalDrinks: drinks.length,
        totalStock,
        lowStock,
        revenue: 0 // Placeholder until category-based sales analytics is available
      };
    }).filter(stat => stat.totalDrinks > 0);
  }

  getLowStockDrinks(): Drink[] {
    return this.drinksSubject.value.filter(drink => drink.stock < 5 && drink.isAvailable);
  }

  /** Set while editing an existing drink; null when adding a new one. */
  editingDrinkId: number | null = null;

  /**
   * Opens the dialog to add a drink, or to edit the one passed in.
   *
   * The drink argument used to be accepted and discarded — the form was always
   * reset — and nothing in the template ever passed one, so a drink's name,
   * price, category and description could not be changed after it was created.
   * The API had supported it all along (PUT /api/v1/drinks/{id}, which persists
   * exactly those fields) and drinksService.updateDrink() was already written;
   * only the screen was missing.
   */
  openAddEditDrinkDialog(drink?: Drink): void {
    this.showAddDrinkDialog = true;
    this.resetNewDrink();
    if (drink) {
      this.editingDrinkId = Number((drink as any).id);
      this.newDrink = {
        ...this.newDrink,
        name: drink.name,
        categoryId: (drink as any).categoryId,
        description: (drink as any).description || '',
        price: (drink as any).price,
        stock: (drink as any).stock,
        imageUrl: (drink as any).imageUrl || '',
        lowStockThreshold: (drink as any).lowStockThreshold ?? 10,
        barId: (drink as any).barId,
        isAvailable: (drink as any).isAvailable !== false,
      } as any;
    }
  }

  closeAddDrinkDialog(): void {
    this.showAddDrinkDialog = false;
    this.editingDrinkId = null;
    this.resetNewDrink();
  }

  resetNewDrink(): void {
    this.newDrink = {
      name: '',
      categoryId: 1, // Default to Beer
      description: '',
      price: 0,
      stock: 0,
      imageUrl: '',
      lowStockThreshold: 10,
      barId: this.barId ?? '',
      isAvailable: true
    };
  }

  addNewDrink(): void {
    if (this.newDrink.name && this.newDrink.categoryId && this.newDrink.price !== undefined && this.newDrink.stock !== undefined) {
      const drinkPayload = {
        name: this.newDrink.name,
        categoryId: this.newDrink.categoryId,
        description: this.newDrink.description || '',
        price: this.newDrink.price,
        stock: this.newDrink.stock,
        imageUrl: this.newDrink.imageUrl || '',
        lowStockThreshold: this.newDrink.lowStockThreshold || 10,
        // Was `|| 'BAR001'` — a developer's fixture id. On a real deployment
        // that bar does not exist, so the tenancy guard rejects the create;
        // the signed-in user's own bar is the only correct value.
        barId: this.newDrink.barId ?? this.barId ?? '',
        isAvailable: this.newDrink.isAvailable !== false
      };

      // Editing and adding submit the same form; the id decides which way it
      // goes. PUT persists name, category, price and description.
      const editing = this.editingDrinkId !== null;
      const request$ = editing
        ? this.drinksService.updateDrink(this.editingDrinkId as number, drinkPayload)
        : this.drinksService.createDrink(drinkPayload);

      request$.subscribe({
        next: () => {
          this.snackBar.open(editing ? 'Drink updated successfully' : 'Drink added successfully',
                             'Close', { duration: 3000 });
          this.loadData(); // Refresh list
          this.closeAddDrinkDialog();
        },
        error: (err) => {
          if (!environment.production) {
            console.error(editing ? 'Error updating drink:' : 'Error adding drink:', err);
          }
          this.snackBar.open(editing ? 'Failed to update drink' : 'Failed to add drink',
                             'Close', { duration: 3000 });
        }
      });
    } else {
      this.snackBar.open('Please fill in all required fields', 'Close', { duration: 3000 });
    }
  }

  openBulkInventoryDialog(): void {
    this.showBulkInventoryDialog = true;
    this.bulkOperation = 'add';
    this.bulkQuantity = 0;
    this.bulkCategoryFilter = '';
    this.dataSource.data.forEach((d: any) => d.selected = false);
  }

  closeBulkInventoryDialog(): void {
    this.showBulkInventoryDialog = false;
  }

  getFilteredDrinks(): any[] {
    let drinks = this.dataSource.data;
    if (this.bulkCategoryFilter) {
      const filterId = typeof this.bulkCategoryFilter === 'number' ? this.bulkCategoryFilter : parseInt(this.bulkCategoryFilter as string);
      if (!isNaN(filterId)) {
        drinks = drinks.filter(drink => drink.categoryId === filterId);
      }
    }
    return drinks;
  }

  applyBulkInventoryUpdate(): void {
    const selectedDrinks = this.dataSource.data.filter((d: any) => d.selected);
    
    if (selectedDrinks.length === 0) {
      this.snackBar.open('No drinks selected', 'Close', { duration: 2000 });
      return;
    }

    if (this.bulkQuantity <= 0) {
      this.snackBar.open('Quantity must be greater than 0', 'Close', { duration: 2000 });
      return;
    }

    this.isLoading = true;
    const updateObservables = selectedDrinks.map(drink => {
      let newStock = drink.stock;
      if (this.bulkOperation === 'add') {
        newStock += this.bulkQuantity;
      } else if (this.bulkOperation === 'subtract') {
        newStock = Math.max(0, newStock - this.bulkQuantity);
      }

      const updatePayload = {
        name: drink.name,
        categoryId: drink.categoryId,
        description: drink.description || '',
        price: drink.price,
        stock: newStock,
        imageUrl: drink.imageUrl || '',
        lowStockThreshold: drink.lowStockThreshold || 10,
        barId: drink.barId ?? this.barId ?? '',
        isAvailable: newStock > 0
      };

      return this.drinksService.updateDrink(drink.id, updatePayload);
    });

    forkJoin(updateObservables).subscribe({
      next: () => {
        this.isLoading = false;
        this.snackBar.open('Bulk update completed successfully', 'Close', { duration: 3000 });
        this.closeBulkInventoryDialog();
        this.loadData();
      },
      error: (err) => {
        this.isLoading = false;
        if (!environment.production) {
          console.error('Error executing bulk update:', err);
        }
        this.snackBar.open('Failed to update some items', 'Close', { duration: 3000 });
      }
    });
  }

  updateStock(drink: Drink, event: any): void {
    const newStock = parseInt(event.target.value);
    if (newStock >= 0) {
      const updatePayload = {
        name: drink.name,
        categoryId: drink.categoryId,
        description: drink.description || '',
        price: drink.price,
        stock: newStock,
        imageUrl: drink.imageUrl || '',
        lowStockThreshold: drink.lowStockThreshold || 10,
        barId: drink.barId ?? this.barId ?? '',
        isAvailable: newStock > 0
      };

      this.drinksService.updateDrink(drink.id, updatePayload).subscribe({
        next: () => {
          drink.stock = newStock;
          drink.isAvailable = newStock > 0;
          this.snackBar.open('Stock updated', 'Close', { duration: 2000 });
        },
        error: (err) => {
          if (!environment.production) {
            console.error('Error updating stock:', err);
          }
          this.snackBar.open('Failed to update stock', 'Close', { duration: 2000 });
        }
      });
    }
  }

  toggleAvailability(drink: Drink): void {
    const newStatus = !drink.isAvailable;
    const updatePayload = {
      name: drink.name,
      categoryId: drink.categoryId,
      description: drink.description || '',
      price: drink.price,
      stock: drink.stock,
      imageUrl: drink.imageUrl || '',
      lowStockThreshold: drink.lowStockThreshold || 10,
      barId: drink.barId ?? this.barId ?? '',
      isAvailable: newStatus
    };

    this.drinksService.updateDrink(drink.id, updatePayload).subscribe({
      next: () => {
        drink.isAvailable = newStatus;
        this.snackBar.open('Availability updated', 'Close', { duration: 2000 });
      },
      error: (err) => {
        if (!environment.production) {
          console.error('Error updating availability:', err);
        }
        this.snackBar.open('Failed to update availability', 'Close', { duration: 2000 });
      }
    });
  }

  deleteDrink(drinkId: number): void {
    this.drinkToDeleteId = drinkId;
    this.dialog.open(this.deleteConfirmDialog, {
      width: '400px',
      panelClass: 'delete-confirm-dialog'
    });
  }

  confirmDelete(): void {
    if (this.drinkToDeleteId) {
      this.drinksService.deleteDrink(this.drinkToDeleteId).subscribe({
        next: () => {
          this.snackBar.open('Drink deleted', 'Close', { duration: 2000 });
          this.loadData();
          this.dialog.closeAll();
          this.drinkToDeleteId = null;
        },
        error: (err) => {
          if (!environment.production) {
            console.error('Error deleting drink:', err);
          }
          this.snackBar.open('Failed to delete drink', 'Close', { duration: 2000 });
          this.dialog.closeAll();
          this.drinkToDeleteId = null;
        }
      });
    }
  }
}